import type RootStore from "stores/RootStore";
import { RouteType } from "types/RouteType";
import { throwIfFailed } from "shared/dialogSubmit";
import type GitIdentitiesStore from "./GitIdentitiesStore";

/**
 * Administration → Git Identities — which git email is whose, for
 * Reports → Developer metrics (see GitIdentitiesStore for the rules).
 *
 * Dialogs are route-registered (the house pattern — see
 * views/administration/ClientAccess/clientAccess.Route.ts).
 */

const storeOf = (rootStore: RootStore) =>
  rootStore.gitIdentitiesStore as unknown as GitIdentitiesStore;

/**
 * A failed write THROWS: the dialog framework keeps a dialog open when its
 * onSubmit rejects and shows the message in the footer, with the form intact.
 * Resolving closes it. (Re-opening it a tick later, as this did, flashed it
 * shut.)
 */
const submitOrThrow = async (
  rootStore: RootStore,
  submit: (store: GitIdentitiesStore) => Promise<boolean>,
  errorOf: (store: GitIdentitiesStore) => string | null,
  fallback: string,
) => {
  const store = storeOf(rootStore);
  throwIfFailed(await submit(store), errorOf(store), fallback);
};

const gitIdentitiesRoute: RouteType = {
  name: "Git Identities",
  title: "Git Identities",
  path: "/git-identities",
  layout: "/app",
  viewportCard: {
    header: {
      enableBackButton: true,
      title: "Git Identities",
      // false: this page loads from its Body, not an onEnter, so the plate's
      // refresh would do nothing. The header renders the working Refresh.
      enableRefreshButton: false,
      color: "primary",
      component: () => import("./GitIdentities.Header"),
    },
    body: {
      component: () => import("./GitIdentities.Body"),
      grayBackground: false,
    },
  },
  group: "Admin",
  searchAliases: [
    "git emails",
    "mailmap",
    "developer identities",
    "developer metrics",
  ],
  requiresAuth: true,
  requiresAdmin: true,
  requiresPermission: false,
  enableAutoFind: false,
  dialogs: [
    {
      name: "GitIdentityDialog",
      title: "Git identity",
      component: () => import("./dialogs/GitIdentityDialog.Body"),
      cancelButton: true,
      maxWidth: "sm",
      height: 440,
      submitButtonText: "Save",
      onSubmit: async ({ rootStore }: { rootStore: RootStore }) =>
        submitOrThrow(
          rootStore,
          (store) => store.submitForm(),
          (store) => store.formError,
          "The git identity could not be saved.",
        ),
      onSubmitValidation: ({ rootStore }: { rootStore: RootStore }) =>
        storeOf(rootStore).canSubmitForm,
    },
    {
      name: "RemoveGitIdentityDialog",
      title: "Remove git identity",
      component: () => import("./dialogs/RemoveGitIdentityDialog.Body"),
      cancelButton: true,
      maxWidth: "xs",
      height: 300,
      submitButtonText: "Remove",
      onSubmit: async ({ rootStore }: { rootStore: RootStore }) =>
        submitOrThrow(
          rootStore,
          (store) => store.executeRemove(),
          (store) => store.removeError,
          "The git identity could not be removed.",
        ),
      onSubmitValidation: ({ rootStore }: { rootStore: RootStore }) => {
        const store = storeOf(rootStore);
        return !!store.removeTarget && !store.removing;
      },
      onClose: ({ rootStore }: { rootStore: RootStore }) =>
        storeOf(rootStore).cancelRemove(),
    },
  ],
};

export default gitIdentitiesRoute;
